import { getFormattedDate } from 'src/utils/date-formatter.util'
import { useTranslation } from 'react-i18next'
import { CurecallList, CurecallTooltip } from '@honestica/curecall-ui'
import { TPatientReviewListColumnName, TPatientReviewList, TPatientReviewListRequest } from './reviews.types'
import { ReactNode } from 'react'
import { TPatientMedEventMetadata } from 'src/types/med-event'
import { useCurrentProject } from 'src/providers/current-project.provider'

const TABLE_COLUMN_LIST: Array<TPatientReviewListColumnName> = [
  // Change the order will affect the table headers order
  'PatientRemoteId',
  'PatientReviewCreatedAt',
  'PatientReviewNumberValue',
  'PatientReviewComment',
  'MedEventLabel',
]

const COLUMN_LABEL_OPTIONS: Partial<Record<TPatientReviewListColumnName, string>> = {
  PatientRemoteId: 'remote-id',
  PatientReviewCreatedAt: 'comment-date',
  PatientReviewNumberValue: 'note',
  PatientReviewComment: 'comment',
  MedEventLabel: 'context-info',
}

const SORTABLE_HEADER_LIST: Array<TPatientReviewListColumnName> = [
  'PatientReviewCreatedAt',
  'PatientReviewNumberValue',
]

const HEADER_CLASSNAME_OPTIONS: Partial<Record<TPatientReviewListColumnName, string>> = {
  PatientRemoteId: 'c-w-24',
  PatientReviewCreatedAt: 'c-w-24',
  PatientReviewNumberValue: 'c-w-12',
  PatientReviewComment: 'c-w-full',
}

interface PatientReviewListTableProps {
  isLoading: boolean
  patientReviewList: TPatientReviewList['patientReviews']
  onTableHeaderClick: (currentOrder: TPatientReviewListRequest['orderBy']) => void
  currentOrder: TPatientReviewListRequest['orderBy']
}

export default function PatientReviewListTable({
  patientReviewList,
  isLoading,
  onTableHeaderClick,
  currentOrder,
}: Readonly<PatientReviewListTableProps>) {
  const { t } = useTranslation()
  const { getMedEventLabelById } = useCurrentProject()

  const PatientReviewValue = (props: { value: number | boolean }) => {
    let review = ''
    if (typeof props.value === 'number') {
      review = `${props.value}/10`
    }
    if (props.value === true) {
      review = t('pages.patient-review.satisfied')
    }
    if (props.value === false) {
      review = t('pages.patient-review.not-satisfied')
    }
    return <div className="c-text-secondary-800 c-font-semibold c-min-w-fit c-w-max">{review}</div>
  }

  const tableRowsData: Array<Partial<Record<TPatientReviewListColumnName, ReactNode>>> =
    patientReviewList.map((patientReview) => ({
      PatientRemoteId: PatientRemoteId({ remoteId: patientReview.Patient.remoteId }),
      PatientReviewCreatedAt: PatientReviewDate({
        date: getFormattedDate(patientReview.createdAt),
      }),
      PatientReviewNumberValue: PatientReviewValue({ value: patientReview.numberValue }),
      PatientReviewComment: PatientReviewComment({ comment: patientReview.comment }),
      MedEventLabel: PatientMedEvent({
        patientMedEventRemoteId: patientReview.Patient.PatientMedEvents[0]?.remoteId,
        medEventLabel: getMedEventLabelById(patientReview.Patient.PatientMedEvents[0]?.MedEventId),
        patientMedEventMetadata: patientReview.Patient.PatientMedEvents[0]?.metadata,
      }),
    }))

  const tableColumnsData = TABLE_COLUMN_LIST.map((header) => ({
    key: header,
    label: t(`pages.patient-review.${COLUMN_LABEL_OPTIONS[header]}`),
    headerClassName: HEADER_CLASSNAME_OPTIONS[header],
    onHeaderClick: SORTABLE_HEADER_LIST.includes(header)
      ? () =>
          onTableHeaderClick({
            column: header,
            descOrder: currentOrder.column === header ? !currentOrder.descOrder : true,
          })
      : undefined,
  }))

  return (
    <CurecallList
      isLoading={isLoading}
      loadingLabel={t('pages.patient-review.loading')}
      noDataLabel={t('pages.patient-review.no-data')}
      rowsData={tableRowsData}
      headersData={tableColumnsData}
      currentOrder={{ key: currentOrder.column, descOrder: currentOrder.descOrder }}
    />
  )
}

const PatientRemoteId = (props: { remoteId: string | undefined }) => (
  <div className="c-font-semibold">{props.remoteId}</div>
)

const PatientReviewDate = (props: { date: string }) => (
  <div className="c-text-grey-600">{String(props.date)}</div>
)

const PatientReviewComment = (props: { comment: string }) => (
  <div className="c-text-black">{props.comment}</div>
)

const PatientMedEvent = (props: {
  patientMedEventRemoteId: string | undefined
  medEventLabel: string | undefined
  patientMedEventMetadata: TPatientMedEventMetadata | undefined
}) => {
  const tooltipText = props.patientMedEventMetadata?.originalLabel

  let functionalUnitLabel = props.patientMedEventMetadata?.functionalUnitId
    ? `UF: ${props.patientMedEventMetadata?.functionalUnitId}`
    : null
  if (props.patientMedEventMetadata?.functionalUnit) {
    functionalUnitLabel += ` - ${props.patientMedEventMetadata?.functionalUnit}`
  }

  return (
    <div className="c-flex c-flex-col">
      <span className="c-font-semibold c-ml-auto c-items-end">{props.patientMedEventRemoteId}</span>
      {tooltipText ? (
        <CurecallTooltip tooltipPosition="left" content={tooltipText} wrapperClassName="tw-items-start">
          <span className="c-text-grey-600">{props.medEventLabel}</span>
        </CurecallTooltip>
      ) : (
        <span className="c-text-grey-600">{props.medEventLabel}</span>
      )}

      {functionalUnitLabel ?? <span className="c-text-grey-600">{functionalUnitLabel}</span>}
    </div>
  )
}
